
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { LearningActivity, ReferencedLearningActivity } from "types";
import { AgendaList } from "./AgendaList.component";
import { Header } from "./Header.component";
import { ContentContainer, EmptyStateText, LoadingWrapper } from "./LearningActivity.styles";
import { useEditions } from "./useEditions.hook";
import { UtilityTabs } from "./UtiliyTabs.component";
import { Favourite } from "../../components/Commons/Favourite.component";
import { CtaOverlay } from "../../components/CtaOverlay";
import { useModal } from "../../components/Modal/useModal.hook";
import { Spinner } from "../../components/Spinner";
import {  useLearningObjectQuery, useToolsQuery } from "../../queries";
import { useGetExercisesList } from "../../queries/exercises";
import { getError } from "../../utils/error";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";

type ILearningActivityContent = {
  activity: ReferencedLearningActivity
  handleClose: (e: React.MouseEvent<HTMLButtonElement>) => void
}

export const LearningActivityVirtualClassroomContent = (({
  activity,
  handleClose
}: ILearningActivityContent) => {

  const { t } = useTranslation();
  const { id, courseId, learningObjectTypology, rootId } = activity;
  const {
    rootIdNumber
  } = useRouteParamsLo();

  const {
    data: learningObject,
    error: learningObjectError,
    isFetching: learningObjectIsFetching
  } = useLearningObjectQuery({
    rootId: rootId,
    courseId: courseId,
    id: id ?? "",
    learningObjectTypology: learningObjectTypology
  });

  const { renderModal, setModalText, setShowModal } = useModal();

  const editionsQueryData = useEditions(learningObject as LearningActivity, setShowModal, setModalText );

  const { data: tools } = useToolsQuery({
    learningObjectId: learningObject?.id?.toString() ?? ""
  });

  const editionBooked = editionsQueryData?.editions?.find(edition => edition.status === "E");

  const { data: exercises } = useGetExercisesList({ editionId: editionBooked?.id });

  if (learningObjectIsFetching) {
    return (
      <LoadingWrapper role="status" aria-live="polite" aria-label={ t("loading", "Caricamento...") }>
        <Spinner />
      </LoadingWrapper>
    );
  }

  if (!learningObject) {
    const errorMessage = getError(learningObjectError)?.[0]?.message
      ?? t("no_data_available");
    return (
      <ContentContainer>
        <Header activity={ { ...activity } as unknown as LearningActivity } handleClose={ handleClose } />
        <EmptyStateText role="alert">{ errorMessage }</EmptyStateText>
      </ContentContainer>
    );
  }

  const {
    isStandAlone,
    isFavourite,
    averageRatingScore,
    certificates,
    badge
  } = learningObject as LearningActivity;

  const agenda = (learningObject as LearningActivity)?.agenda;

  return (
    <ContentContainer>
      <Header
        activity={ learningObject as LearningActivity }
        handleClose={ handleClose }
      />

      { /* description activity & domains */ }
      <Stack>

        <Typography
          fontSize={ "0.875rem" }
          letterSpacing={ "0.0156rem" }
        >
          { learningObject?.longDescription ?? learningObject?.shortDescription ?? "" }
        </Typography>
        { /* <DomainsList /> */ }
      </Stack>
      { /* programma / agendas */ }
      { agenda?.length > 0 && (
        <Stack>
          <AgendaList agenda={ (learningObject as LearningActivity)?.agenda } />
        </Stack>
      ) }

      { /* CTA */ }
      <Stack
        flexDirection={ "row" }
        justifyContent={ "space-between" }
        position={ "relative" }
        sx={ {
          "& #cta-overlay-toggle": {
            position: "unset"
          }
        } }
      >
        { isStandAlone && (
          <Favourite
            isStandAlone={ isStandAlone }
            id={ id }
            rootId={ rootIdNumber }
            courseId={ courseId }
            learningObjectTypology={ learningObjectTypology }
            isFavourite={ isFavourite }
          />
        ) }
        <CtaOverlay
          data={ {
            isStandAlone: isStandAlone,
            isFavourite: isFavourite,
            id: id,
            learningObjectTypology: learningObjectTypology,
            rating: averageRatingScore ?? 0,
            courseId: courseId,
            rootId: rootIdNumber
          } }
          certificates={ certificates }
          badge={ badge }
          iconOnly={ true }
          sxContainer={ {
            position: "unset"
          } }
          keepIconsInMobile={ true }
        />
      </Stack>

      <UtilityTabs
        learningObject={ learningObject as LearningActivity }
        tools={ tools ?? [] }
        exercises={ exercises ?? [] }
        editionsQueryData={ editionsQueryData }
      />
      { renderModal(sxStyle) }
    </ContentContainer>
  );
});

const sxStyle = ()=> ({
  width: "31rem",
  ".MuiTypography-root":{
    maxWidth:"unset",
    textAlign:"center"
  }
});
